import { createClient } from '@/lib/supabase/client';

/**
 * Confirmed bookings, stored where the admin dashboard can read them.
 *
 * A booking used to leave an email and a localStorage entry in the visitor's
 * browser — so the dashboard, running in a different browser entirely, had
 * nothing to show, and everything the client filled in lived only inside one
 * email.
 */

export interface Booking {
  id: string;
  name: string;
  email: string;
  company: string;
  industry: string;
  stage: string;
  challenge: string;
  message: string;
  consultationType: string;
  duration: string;
  date: string;
  time: string;
  timezone: string;
  status: 'confirmed' | 'completed' | 'cancelled' | string;
  emailDelivered: boolean;
  createdAt: string;
}

export const BOOKINGS_TABLE = 'bookings';

export function rowToBooking(row: Record<string, unknown>): Booking {
  return {
    id: String(row.id ?? ''),
    name: String(row.name ?? ''),
    email: String(row.email ?? ''),
    company: String(row.company ?? ''),
    industry: String(row.industry ?? ''),
    stage: String(row.stage ?? ''),
    challenge: String(row.challenge ?? ''),
    message: String(row.message ?? ''),
    consultationType: String(row.consultation_type ?? ''),
    duration: String(row.duration ?? ''),
    date: String(row.booking_date ?? ''),
    time: String(row.booking_time ?? ''),
    timezone: String(row.timezone ?? ''),
    status: String(row.status ?? 'confirmed'),
    emailDelivered: Boolean(row.email_delivered),
    createdAt: String(row.created_at ?? ''),
  };
}

/**
 * Records a confirmed booking. Never throws and never blocks the visitor: the
 * slot is already theirs and the notification email is sent separately, so a
 * storage failure must not turn a completed booking into an error screen. It is
 * reported to the console for diagnosis instead.
 */
export async function saveBooking(
  booking: Omit<Booking, 'id' | 'createdAt' | 'status'>
): Promise<string | null> {
  const supabase = createClient();
  if (!supabase) return 'Supabase is not configured.';
  try {
    const { error } = await supabase.from(BOOKINGS_TABLE).insert({
      name: booking.name,
      email: booking.email,
      company: booking.company,
      industry: booking.industry,
      stage: booking.stage,
      challenge: booking.challenge,
      message: booking.message,
      consultation_type: booking.consultationType,
      duration: booking.duration,
      booking_date: booking.date,
      booking_time: booking.time,
      timezone: booking.timezone,
      email_delivered: booking.emailDelivered,
    });
    if (error) {
      console.error('Could not record the booking:', error.message);
      return error.message;
    }
    return null;
  } catch (thrown) {
    // supabase-js rejects rather than resolving when the request itself fails.
    console.error('Could not reach the booking store:', thrown);
    return thrown instanceof Error ? thrown.message : 'Network error';
  }
}

export async function loadBookings(): Promise<{ bookings: Booking[]; error: string }> {
  const supabase = createClient();
  if (!supabase) return { bookings: [], error: 'Supabase is not configured.' };
  try {
    const { data, error } = await supabase
      .from(BOOKINGS_TABLE)
      .select('*')
      .order('created_at', { ascending: false });
    if (error) return { bookings: [], error: error.message };
    return { bookings: (data ?? []).map(rowToBooking), error: '' };
  } catch (thrown) {
    return { bookings: [], error: thrown instanceof Error ? thrown.message : 'Network error' };
  }
}

export async function setBookingStatus(id: string, status: string): Promise<string | null> {
  const supabase = createClient();
  if (!supabase) return 'Supabase is not configured.';
  const { error } = await supabase.from(BOOKINGS_TABLE).update({ status }).eq('id', id);
  return error ? error.message : null;
}

export async function deleteBooking(id: string): Promise<string | null> {
  const supabase = createClient();
  if (!supabase) return 'Supabase is not configured.';
  const { error } = await supabase.from(BOOKINGS_TABLE).delete().eq('id', id);
  return error ? error.message : null;
}
